/**
 * nav-mapping — 라우트 앞부분(prefix) 기준 "소속" 판정 (스튜디오 개선 설계서 §9 FE 설계).
 *
 * 좌측 내비가 정적으로 고정되면서(activeAgent 분기 제거), 지금 보고 있는 화면이
 * 좌측 메뉴의 어느 항목/그룹 소속인지, 브레드크럼에 뭐라고 표시할지를 이 파일이
 * 단일하게 판정한다. LeftNav(active 하이라이트)와 TopBar(브레드크럼)가 공유한다.
 */
import { AGENT_MENU_CONFIG } from './menu-config';

export interface NavOwner {
  /** 이 소유권이 적용되는 라우트 접두사 (pathname.startsWith 기준) */
  prefix: string;
  /** 안정적인 식별자 — LeftNav active 판정에 사용 */
  ownerId: string;
  /** 좌측 메뉴/브레드크럼에 표시할 라벨 (예: "나의 사진첩") */
  ownerLabel: string;
  /** 좌측 메뉴 그룹명 (예: "작업 보관함") */
  groupLabel: string;
}

// 소속 매핑표 (FR-06) — 진입 맥락(어느 화면에서 눌러 들어왔는지)과 무관하게
// 라우트 앞부분만으로 판정한다.
export const NAV_OWNERS: NavOwner[] = [
  { prefix: '/agents/photo-organizer', ownerId: 'photo-organizer', ownerLabel: '나의 사진첩',   groupLabel: '작업 보관함' },
  { prefix: '/agents/portfolio',       ownerId: 'portfolio',       ownerLabel: '나의 시공사례', groupLabel: '작업 보관함' },
  { prefix: '/agents/scheduler',       ownerId: 'scheduler',       ownerLabel: '나의 공사스케줄', groupLabel: '작업 보관함' },
  { prefix: '/agents/work-log',        ownerId: 'work-log',        ownerLabel: '나의 공사현황', groupLabel: '작업 보관함' },
];

export function resolveNavOwner(pathname: string): NavOwner | null {
  return NAV_OWNERS.find((o) => pathname.startsWith(o.prefix)) ?? null;
}

function matchMenuPath(menuPath: string, pathname: string, search: string): boolean {
  const [mp, mq] = menuPath.split('?');
  if (pathname !== mp) return false;
  if (mq) return search === `?${mq}`;
  return !search;
}

// 세부기능 라우트별 브레드크럼 화면명 오버라이드 (FR-10) — 여기 없으면 아래
// AGENT_MENU_CONFIG의 children 라벨로 폴백한다.
const SCREEN_NAME_OVERRIDES: Record<string, string> = {
  '/agents/photo-organizer/classify-site': '현장 분류',
  '/agents/photo-organizer/tag-bulk-edit': '태그 편집',
  '/agents/photo-organizer/share-send':    '공유 보내기',
  '/agents/photo-organizer/photo-detail':  '사진 상세',
};
const SCREEN_NAME_PATTERNS: { test: RegExp; label: string }[] = [
  { test: /^\/agents\/photo-organizer\/site\/\d+$/, label: '현장 상세' },
];

/** 소속 매핑에 걸리는 라우트의 3번째(세부) 브레드크럼 라벨. 소속이 없으면 null. */
export function resolveScreenName(pathname: string, search: string): string | null {
  if (SCREEN_NAME_OVERRIDES[pathname]) return SCREEN_NAME_OVERRIDES[pathname];
  for (const p of SCREEN_NAME_PATTERNS) if (p.test.test(pathname)) return p.label;

  const owner = resolveNavOwner(pathname);
  if (!owner) return null;
  const agent = AGENT_MENU_CONFIG.find((a) => a.basePath === owner.prefix);
  if (!agent) return null;
  for (const item of agent.children) {
    if (item.children) {
      for (const sub of item.children) {
        if (matchMenuPath(sub.path, pathname, search)) return sub.label;
      }
    } else if (matchMenuPath(item.path, pathname, search)) {
      return item.label;
    }
  }
  return null;
}
